import React, { useState, useEffect } from 'react';
import { Smartphone, MessageSquare, Key, ArrowLeft } from 'lucide-react';
import apiService from '../services/api';

interface TwoFactorVerificationModalProps {
  open: boolean;
  userId: number;
  availableMethods: 'app' | 'sms' | 'both';
  phoneNumber?: string;
  onSuccess: (code: string, method: string, rememberDevice: boolean) => void;
  onCancel: () => void;
}

const TwoFactorVerificationModal: React.FC<TwoFactorVerificationModalProps> = ({
  open,
  userId,
  availableMethods,
  phoneNumber,
  onSuccess,
  onCancel,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<'app' | 'sms' | 'backup'>(
    availableMethods === 'sms' ? 'sms' : 'app'
  );
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
 const [smsResendTimer, setSmsResendTimer] = useState(0);
  const [rememberDevice, setRememberDevice] = useState(false);

  // Hover/focus states
  const [codeFocused, setCodeFocused] = useState(false);
  const [verifyHovered, setVerifyHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [resendHovered, setResendHovered] = useState(false);
  const [backupHovered, setBackupHovered] = useState(false);

  useEffect(() => {
    if (smsResendTimer > 0) {
      const timer = setTimeout(() => setSmsResendTimer(smsResendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsResendTimer]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCode('');
      setError(null);
      setSelectedMethod(availableMethods === 'sms' ? 'sms' : 'app');
    }
  }, [open, availableMethods]);

const handleVerify = () => {
    onSuccess(code, selectedMethod, rememberDevice);
  };

  const handleResendSMS = async () => {
    setError(null);
    setLoading(true);

    try {
      await apiService.post('/auth/2fa/resend-sms', { userId });
      setSmsResendTimer(60);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (selectedMethod === 'backup') {
      setCode(value.toUpperCase());
    } else {
      setCode(value.replace(/\D/g, ''));
    }
  };

  if (!open) return null;

  const isVerifyDisabled = !code || (selectedMethod !== 'backup' && code.length < 6);

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 1000,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-lg)',
    padding: '32px',
    width: '100%',
    maxWidth: '400px',
    fontFamily: 'var(--font-sans)',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    margin: '0 0 8px 0',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--v2-text-tertiary)',
    margin: '0 0 24px 0',
  };

  const alertStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderRadius: 'var(--radius-md)',
    borderLeft: '4px solid var(--v2-error)',
    backgroundColor: 'var(--v2-error-bg)',
    color: 'var(--v2-error)',
    fontSize: '0.875rem',
    marginBottom: '20px',
  };

  const toggleContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    backgroundColor: 'var(--v2-bg)',
    borderRadius: 'var(--radius-md)',
    marginBottom: '24px',
  };

  const getToggleButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '10px 12px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: isSelected ? '#ffffff' : 'var(--v2-text-tertiary)',
    backgroundColor: isSelected ? 'var(--v2-primary)' : 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  });

  const methodInfoStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: 'var(--v2-surface-alt)',
    borderRadius: 'var(--radius-md)',
  };

  const methodIconContainerStyle: React.CSSProperties = {
    width: '48px',
    height: '48px',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-primary-light)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px',
  };

  const methodTextStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--v2-text-tertiary)',
    margin: 0,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--v2-text)',
    marginBottom: '6px',
  };

  const getInputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '12px 14px',
    fontSize: '1.0625rem',
    fontFamily: 'var(--font-mono)',
    letterSpacing: '0.15em',
    textAlign: 'center',
    border: focused ? '1px solid var(--v2-primary)' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxSizing: 'border-box',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
  });

  const getSecondaryButtonStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 16px',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: disabled ? 'var(--v2-text-muted)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    backgroundColor: 'transparent',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s ease',
    marginTop: '12px',
  });

  const backupLinkStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: backupHovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: backupHovered ? 'underline' : 'none',
    padding: 0,
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    width: '100%',
  };

  const actionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  };

  const getCancelButtonStyle = (hovered: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '12px 20px',
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: hovered ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
    backgroundColor: hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  const getVerifyButtonStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '12px 20px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s ease',
  });

  const getMethodInfo = () => {
    switch (selectedMethod) {
      case 'app':
        return { icon: Smartphone, text: 'Enter code from authenticator app' };
      case 'sms':
        return { icon: MessageSquare, text: `Enter code sent to ${phoneNumber || 'your phone'}` };
      case 'backup':
        return { icon: Key, text: 'Enter a backup code' };
    }
  };

  const methodInfo = getMethodInfo();
  const MethodIcon = methodInfo.icon;

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>Two-Factor Authentication</h2>
        <p style={subtitleStyle}>Enter your verification code</p>

        {error && <div style={alertStyle}>{error}</div>}

        {/* Method toggle - only show if both methods available */}
        {availableMethods === 'both' && (
          <div style={toggleContainerStyle}>
            <button
              style={getToggleButtonStyle(selectedMethod === 'app')}
              onClick={() => setSelectedMethod('app')}
            >
              <Smartphone size={16} strokeWidth={1.5} /> App
            </button>
            <button
              style={getToggleButtonStyle(selectedMethod === 'sms')}
              onClick={() => setSelectedMethod('sms')}
            >
              <MessageSquare size={16} strokeWidth={1.5} /> SMS
            </button>
            <button
              style={getToggleButtonStyle(selectedMethod === 'backup')}
              onClick={() => setSelectedMethod('backup')}
            >
              <Key size={16} strokeWidth={1.5} /> Backup
            </button>
          </div>
        )}

        {/* Backup code toggle - show if only one method available */}
        {availableMethods !== 'both' && (
          <button
            style={backupLinkStyle}
            onClick={() => setSelectedMethod(selectedMethod === 'backup' ? availableMethods : 'backup')}
            onMouseEnter={() => setBackupHovered(true)}
            onMouseLeave={() => setBackupHovered(false)}
          >
            {selectedMethod === 'backup' ? (
              <>
                <ArrowLeft size={14} strokeWidth={1.5} /> Use regular code
              </>
            ) : (
              'Use backup code instead'
            )}
          </button>
        )}

        {/* Method info */}
        <div style={methodInfoStyle}>
          <div style={methodIconContainerStyle}>
            <MethodIcon size={24} strokeWidth={1.5} color="var(--v2-primary)" />
          </div>
          <p style={methodTextStyle}>{methodInfo.text}</p>
        </div>

        {/* Code input */}
        <div>
          <label style={labelStyle}>Verification Code</label>
          <input
            type="text"
            value={code}
            onChange={handleCodeChange}
            onFocus={() => setCodeFocused(true)}
            onBlur={() => setCodeFocused(false)}
            placeholder={selectedMethod === 'backup' ? '8 characters' : '6 digits'}
            maxLength={selectedMethod === 'backup' ? 8 : 6}
            autoComplete="one-time-code"
            autoFocus
            style={getInputStyle(codeFocused)}
          />
        </div>

{/* Remember device checkbox - not shown for backup codes */}
        {selectedMethod !== 'backup' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
            <input
              type="checkbox"
              id="rememberDevice"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--v2-primary)' }}
            />
            <label
              htmlFor="rememberDevice"
              style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', cursor: 'pointer', userSelect: 'none' }}
            >
              Don't ask again on this device for 30 days
            </label>
          </div>
        )}

        {/* Resend SMS button */}
        {selectedMethod === 'sms' && (
          <button
            onClick={handleResendSMS}
            disabled={smsResendTimer > 0 || loading}
            onMouseEnter={() => setResendHovered(true)}
            onMouseLeave={() => setResendHovered(false)}
            style={getSecondaryButtonStyle(resendHovered, smsResendTimer > 0 || loading)}
          >
            {smsResendTimer > 0 ? `Resend in ${smsResendTimer}s` : 'Resend SMS'}
          </button>
        )}

        {/* Actions */}
        <div style={actionsStyle}>
          <button
            onClick={onCancel}
            onMouseEnter={() => setCancelHovered(true)}
            onMouseLeave={() => setCancelHovered(false)}
            style={getCancelButtonStyle(cancelHovered)}
          >
            Cancel
          </button>
          <button
            onClick={handleVerify}
            disabled={isVerifyDisabled}
            onMouseEnter={() => setVerifyHovered(true)}
            onMouseLeave={() => setVerifyHovered(false)}
            style={getVerifyButtonStyle(verifyHovered, isVerifyDisabled)}
          >
            Verify
          </button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerificationModal;
